/**
 * Navmesh - navegacao dos bots.
 *
 * Nao existe malha de navegacao autoral no projeto: ela e DERIVADA do cenario,
 * uma unica vez, no init(). O metodo e uma grade regular 2.5D (heightfield):
 * para cada celula amostramos o chao com ctx.collision.sampleGround e guardamos
 * altura + caminhavel. Parede vira obstaculo sozinha, porque a amostra em cima
 * dela devolve o TOPO da parede e a diferenca de altura para a vizinha estoura o
 * degrau maximo - ou seja, nao precisamos marcar parede a mao.
 *
 * Tudo mora em arrays tipados (Uint8/Float32/Int32). Uma grade de 120x120 e
 * 14.400 celulas: 57 KB por array. Barato de percorrer, zero pressao no GC.
 *
 * O A* devolve um caminho de CELULAS, que em seguida passa por duas etapas de
 * acabamento, e sao elas que decidem se o bot parece soldado ou parece robo:
 *   1. line-of-sight pull: corta pontos intermediarios que estao em linha reta
 *      livre. Sem isso o caminho fica em ziguezague de 45 graus.
 *   2. afastamento de parede: empurra cada waypoint para longe de celulas
 *      bloqueadas, para o bot nao raspar a quina e travar na colisao.
 */

import * as THREE from 'three';
import { PLAYER } from '../core/Constants.js';

/** Tamanho de celula alvo (m). ~120x120 celulas num mapa de 90x90 m. */
const CELL_SIZE = 0.75;
/** Teto de celulas por eixo: mapa maior aumenta a celula em vez de estourar custo. */
const MAX_AXIS = 200;
/** Subida maxima entre vizinhas - mesmo degrau do jogador. */
const STEP_UP = PLAYER.STEP_HEIGHT;
/** Queda tolerada entre vizinhas (cair de um meio-fio ou de um caixote). */
const STEP_DOWN = 1.3;
/** Altura livre exigida acima do chao para um bot em pe. */
const HEADROOM = 1.55;
const SLOPE_MIN_COS = Math.cos((PLAYER.MAX_SLOPE_DEG * Math.PI) / 180);
/** Meia-largura do bot, usada na suavizacao para nao cortar quina. */
const AGENT_HALF = 0.42;
/** Teto de expansoes por busca. Protege o frame de um A* patologico. */
const MAX_EXPAND = 6000;
/** Altura sem chao valido. Marca celula como "vazio". */
const NO_GROUND = -9999;

const SQRT2 = Math.SQRT2;

// --- temporarios de modulo: nenhuma alocacao no caminho quente ---------------
const _up = new THREE.Vector3(0, 1, 0);
const _pa = new THREE.Vector3();
const _pb = new THREE.Vector3();

// buffers fixos da busca de cobertura (uma busca por vez, sem alocar)
const CAND_MAX = 48;
const _candIdx = new Int32Array(CAND_MAX);
const _candScore = new Float32Array(CAND_MAX);
/**
 * Pool de resultados de cobertura. coverPointsNear devolve REFERENCIAS a estes
 * vetores - quem chama tem que COPIAR o ponto escolhido antes da proxima busca.
 * E o preco de nao alocar Vector3 dentro do passo fixo.
 */
const COVER_MAX = 5;
const _coverPool = [];
for (let i = 0; i < COVER_MAX; i++) _coverPool.push(new THREE.Vector3());

export class Navmesh {
  constructor(ctx) {
    this.ctx = ctx;
    this.ready = false;

    this.cell = CELL_SIZE;
    this.invCell = 1 / CELL_SIZE;
    this.cols = 0;
    this.rows = 0;
    this.minX = 0;
    this.minZ = 0;
    this.sampleTop = 50;

    /** @type {Uint8Array|null} 1 = caminhavel */
    this.walk = null;
    /** @type {Float32Array|null} altura do chao por celula */
    this.height = null;
    /** @type {Uint8Array|null} vizinhos bloqueados (0..8) - custo extra perto de parede */
    this.penalty = null;
    /** @type {Int32Array|null} componente conexa (ilhas nao se comunicam) */
    this.region = null;

    // estruturas do A*
    this._g = null;
    this._from = null;
    this._stamp = null;
    this._closed = null;
    this._heapIdx = null;
    this._heapKey = null;
    this._heapSize = 0;
    this._searchId = 0;
    this._cells = null;
    this._queue = null;

    this._warned = false;
    this.stats = { cells: 0, walkable: 0, regions: 0, buildMs: 0 };
  }

  // =========================================================================
  // construcao
  // =========================================================================

  async init() {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : 0);
    this._computeExtents();
    this._allocate();
    this._sampleGrid();
    this._computePenalty();
    this._buildRegions();
    this.ready = true;
    this.stats.buildMs = (typeof performance !== 'undefined' ? performance.now() : 0) - t0;
    return this;
  }

  _computeExtents() {
    const b = this.ctx.level?.bounds;
    let minX = -48, maxX = 48, minZ = -48, maxZ = 48, maxY = 40;
    if (b && b.min && b.max && Number.isFinite(b.min.x) && Number.isFinite(b.max.x) && b.max.x > b.min.x) {
      minX = b.min.x; maxX = b.max.x;
      minZ = b.min.z; maxZ = b.max.z;
      if (Number.isFinite(b.max.y)) maxY = b.max.y;
    }
    // uma celula de folga para fora, para a borda do mapa ainda ter vizinho
    minX -= CELL_SIZE; minZ -= CELL_SIZE;
    maxX += CELL_SIZE; maxZ += CELL_SIZE;

    const spanX = Math.max(4, maxX - minX);
    const spanZ = Math.max(4, maxZ - minZ);
    let cell = CELL_SIZE;
    const worstAxis = Math.max(spanX, spanZ);
    if (worstAxis / cell > MAX_AXIS) cell = worstAxis / MAX_AXIS;

    this.cell = cell;
    this.invCell = 1 / cell;
    this.minX = minX;
    this.minZ = minZ;
    this.cols = Math.max(2, Math.ceil(spanX / cell));
    this.rows = Math.max(2, Math.ceil(spanZ / cell));
    this.sampleTop = maxY + 6;
  }

  _allocate() {
    const n = this.cols * this.rows;
    this.walk = new Uint8Array(n);
    this.height = new Float32Array(n);
    this.penalty = new Uint8Array(n);
    this.region = new Int32Array(n);

    this._g = new Float32Array(n);
    this._from = new Int32Array(n);
    this._stamp = new Int32Array(n);
    this._closed = new Int32Array(n);
    // heap com folga: o A* usa insercao preguicosa (mesma celula pode entrar mais de uma vez)
    this._heapIdx = new Int32Array(n * 2 + 64);
    this._heapKey = new Float32Array(n * 2 + 64);
    this._cells = new Int32Array(n);
    this._queue = new Int32Array(n);
    this.stats.cells = n;
  }

  _sampleGrid() {
    const col = this.ctx.collision;
    const hasGround = typeof col?.sampleGround === 'function';
    const hasRay = typeof col?.raycast === 'function';

    if (!hasGround) {
      // Degradacao silenciosa: sem sondagem de chao a grade vira um plano em y=0.
      // O bot ainda anda (quem resolve colisao de verdade e o capsuleMove), so
      // perde a inteligencia de contornar obstaculo.
      this._warnOnce('CollisionWorld.sampleGround indisponivel - navegacao em modo plano');
      this.walk.fill(1);
      this.height.fill(0);
      this.stats.walkable = this.walk.length;
      return;
    }

    const { cols, rows, cell, minX, minZ } = this;
    let walkable = 0;
    for (let z = 0; z < rows; z++) {
      const wz = minZ + (z + 0.5) * cell;
      const rowBase = z * cols;
      for (let x = 0; x < cols; x++) {
        const i = rowBase + x;
        const wx = minX + (x + 0.5) * cell;

        let g = null;
        try {
          g = col.sampleGround(wx, wz, this.sampleTop);
        } catch (err) {
          g = null;
        }
        if (!g || !Number.isFinite(g.y)) {
          this.walk[i] = 0;
          this.height[i] = NO_GROUND;
          continue;
        }

        this.height[i] = g.y;

        // rampa ingreme demais nao e caminhavel
        const ny = g.normal ? g.normal.y : 1;
        if (ny < SLOPE_MIN_COS) { this.walk[i] = 0; continue; }

        // teto baixo: debaixo de escada, de carro ou dentro de duto nao serve
        if (hasRay) {
          _pa.set(wx, g.y + 0.22, wz);
          let hit = null;
          try {
            hit = col.raycast(_pa, _up, HEADROOM);
          } catch (err) {
            hit = null;
          }
          if (hit) { this.walk[i] = 0; continue; }
        }

        this.walk[i] = 1;
        walkable++;
      }
    }
    this.stats.walkable = walkable;
  }

  /** Conta vizinhos bloqueados. Vira custo extra: bot prefere o meio da rua a raspar parede. */
  _computePenalty() {
    const { cols, rows } = this;
    for (let z = 0; z < rows; z++) {
      for (let x = 0; x < cols; x++) {
        const i = z * cols + x;
        if (!this.walk[i]) { this.penalty[i] = 8; continue; }
        let blocked = 0;
        for (let dz = -1; dz <= 1; dz++) {
          const nz = z + dz;
          if (nz < 0 || nz >= rows) { blocked += 3; break; }
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dz === 0) continue;
            const nx = x + dx;
            if (nx < 0 || nx >= cols) { blocked++; continue; }
            const j = nz * cols + nx;
            if (!this.walk[j] || Math.abs(this.height[j] - this.height[i]) > STEP_UP) blocked++;
          }
        }
        this.penalty[i] = Math.min(8, blocked);
      }
    }
  }

  /**
   * Componentes conexas. Serve para nao mandar bot buscar destino de outra ilha
   * (telhado, topo de muro) - o A* gastaria a busca inteira para falhar.
   * Ligacao aqui e simetrica (|dh| <= degrau), mais conservadora que a do A*.
   */
  _buildRegions() {
    const { cols, rows } = this;
    const n = cols * rows;
    this.region.fill(-1);
    const q = this._queue;
    let regions = 0;

    for (let seed = 0; seed < n; seed++) {
      if (!this.walk[seed] || this.region[seed] !== -1) continue;
      const id = regions++;
      let head = 0, tail = 0;
      q[tail++] = seed;
      this.region[seed] = id;
      while (head < tail) {
        const cur = q[head++];
        const cx = cur % cols;
        const cz = (cur / cols) | 0;
        const hc = this.height[cur];
        for (let dz = -1; dz <= 1; dz++) {
          const nz = cz + dz;
          if (nz < 0 || nz >= rows) continue;
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dz === 0) continue;
            const nx = cx + dx;
            if (nx < 0 || nx >= cols) continue;
            const j = nz * cols + nx;
            if (!this.walk[j] || this.region[j] !== -1) continue;
            if (Math.abs(this.height[j] - hc) > STEP_UP) continue;
            this.region[j] = id;
            q[tail++] = j;
          }
        }
      }
    }
    this.stats.regions = regions;
  }

  _warnOnce(msg) {
    if (this._warned) return;
    this._warned = true;
    console.warn(`[Navmesh] ${msg}`);
  }

  // =========================================================================
  // consultas basicas
  // =========================================================================

  cellIndexAt(x, z) {
    const cx = Math.floor((x - this.minX) * this.invCell);
    const cz = Math.floor((z - this.minZ) * this.invCell);
    if (cx < 0 || cz < 0 || cx >= this.cols || cz >= this.rows) return -1;
    return cz * this.cols + cx;
  }

  cellCenterX(i) { return this.minX + ((i % this.cols) + 0.5) * this.cell; }
  cellCenterZ(i) { return this.minZ + (((i / this.cols) | 0) + 0.5) * this.cell; }

  /** Altura do chao numa posicao do mundo, ou NaN se nao houver celula. */
  heightAt(x, z) {
    const i = this.cellIndexAt(x, z);
    if (i < 0) return NaN;
    const h = this.height[i];
    return h === NO_GROUND ? NaN : h;
  }

  /** Contrato: isWalkable(pos) -> bool. */
  isWalkable(pos) {
    if (!this.ready || !pos) return false;
    const i = this.cellIndexAt(pos.x, pos.z);
    if (i < 0 || !this.walk[i]) return false;
    // se a posicao vem com altura, exige coerencia (nao valer telhado por baixo)
    if (Number.isFinite(pos.y) && Math.abs(this.height[i] - pos.y) > 1.6) return false;
    return true;
  }

  regionAt(x, z, y) {
    const i = this._findCell(x, z, y, 4);
    return i < 0 ? -1 : this.region[i];
  }

  /**
   * Celula caminhavel mais proxima. Busca em aneis crescentes; entre empates
   * prefere a de altura parecida com y (evita pular para o telhado de cima).
   */
  _findCell(x, z, y, maxRing = 6) {
    const cx = Math.floor((x - this.minX) * this.invCell);
    const cz = Math.floor((z - this.minZ) * this.invCell);
    const useY = Number.isFinite(y);
    let best = -1;
    let bestScore = Infinity;
    for (let r = 0; r <= maxRing; r++) {
      for (let dz = -r; dz <= r; dz++) {
        const nz = cz + dz;
        if (nz < 0 || nz >= this.rows) continue;
        const edge = (dz === -r || dz === r);
        for (let dx = -r; dx <= r; dx++) {
          if (!edge && dx !== -r && dx !== r) continue; // so a borda do anel
          const nx = cx + dx;
          if (nx < 0 || nx >= this.cols) continue;
          const j = nz * this.cols + nx;
          if (!this.walk[j]) continue;
          let score = dx * dx + dz * dz;
          if (useY) score += Math.abs(this.height[j] - y) * 6;
          if (score < bestScore) { bestScore = score; best = j; }
        }
      }
      if (best >= 0) return best;
    }
    return best;
  }

  /** Encosta uma posicao arbitraria na celula caminhavel mais proxima. */
  snap(pos, out = null, maxRing = 8) {
    const dst = out || new THREE.Vector3();
    if (!this.ready) return dst.copy(pos);
    const i = this._findCell(pos.x, pos.z, pos.y, maxRing);
    if (i < 0) return dst.copy(pos);
    dst.set(this.cellCenterX(i), this.height[i], this.cellCenterZ(i));
    return dst;
  }

  /**
   * Quantos vizinhos daquela celula sao bloqueados (0..8). E a medida barata de
   * EXPOSICAO: 0 = meio da rua, sem nada em volta; 4+ = encostado em parede,
   * container ou quina. Zero custo (leitura de array), sem raycast.
   */
  exposureAt(x, z) {
    if (!this.ready) return 0;
    const i = this.cellIndexAt(x, z);
    if (i < 0) return 0;
    return this.penalty[i];
  }

  /**
   * Normal aproximada do chao, por diferenca finita na grade de altura ja
   * amostrada no init. Escreve em `out` e devolve `out` - ZERO alocacao, porque
   * quem chama e o decalque de sombra, uma vez por bot por frame.
   *
   * Por que nao chamar collision.sampleGround: aquele metodo devolve objeto novo
   * (com Vector3 dentro) a cada chamada. Um por bot por frame e lixo garantido no
   * laco quente. A grade ja tem a informacao de graca.
   *
   * Degrau vira plano de proposito: numa escada a diferenca entre celulas
   * vizinhas e a altura do degrau inteiro, e a normal sairia deitada 45 graus.
   */
  slopeAt(x, z, out) {
    out.set(0, 1, 0);
    if (!this.ready) return out;
    const i = this.cellIndexAt(x, z);
    if (i < 0 || !this.walk[i]) return out;
    const cx = i % this.cols;
    const cz = (i / this.cols) | 0;
    const h = this.height[i];
    const dhx = this._heightDelta(cx - 1, cz, cx + 1, cz, h);
    const dhz = this._heightDelta(cx, cz - 1, cx, cz + 1, h);
    if (dhx === 0 && dhz === 0) return out;
    out.set(-dhx, 2 * this.cell, -dhz).normalize();
    return out;
  }

  /** Diferenca de altura entre duas celulas; 0 se for degrau ou celula invalida. */
  _heightDelta(ax, az, bx, bz, fallback) {
    const ha = this._heightOf(ax, az, fallback);
    const hb = this._heightOf(bx, bz, fallback);
    const d = hb - ha;
    return (d > STEP_UP || d < -STEP_UP) ? 0 : d;
  }

  _heightOf(cx, cz, fallback) {
    if (cx < 0 || cz < 0 || cx >= this.cols || cz >= this.rows) return fallback;
    const i = cz * this.cols + cx;
    if (!this.walk[i]) return fallback;
    const h = this.height[i];
    return h === NO_GROUND ? fallback : h;
  }

  // =========================================================================
  // ponto aleatorio
  // =========================================================================

  /**
   * Contrato: randomPoint(near, radius) -> Vector3 (ou null).
   * So devolve ponto da MESMA regiao de `near`: ponto de outra ilha faz o bot
   * andar em circulos tentando alcancar algo inalcancavel.
   */
  randomPoint(near, radius = 14, out = null) {
    if (!this.ready) return null;
    const dst = out || new THREE.Vector3();
    const startCell = this._findCell(near.x, near.z, near.y, 6);
    if (startCell < 0) return null;
    const reg = this.region[startCell];

    for (let attempt = 0; attempt < 32; attempt++) {
      const ang = Math.random() * Math.PI * 2;
      // sqrt para distribuir uniforme na area, com um piso para nao escolher o proprio pe
      const rad = radius * (0.25 + 0.75 * Math.sqrt(Math.random()));
      const x = near.x + Math.cos(ang) * rad;
      const z = near.z + Math.sin(ang) * rad;
      const i = this.cellIndexAt(x, z);
      if (i < 0 || !this.walk[i] || this.region[i] !== reg) continue;
      if (this.penalty[i] >= 6) continue; // canto apertado nao serve de destino
      dst.set(this.cellCenterX(i), this.height[i], this.cellCenterZ(i));
      return dst;
    }

    // plano B: qualquer vizinha valida em anel maior
    const i = this._findCell(near.x + (Math.random() - 0.5) * radius, near.z + (Math.random() - 0.5) * radius, near.y, 10);
    if (i < 0 || this.region[i] !== reg) return null;
    dst.set(this.cellCenterX(i), this.height[i], this.cellCenterZ(i));
    return dst;
  }

  // =========================================================================
  // A*
  // =========================================================================

  /** Contrato: findPath(from, to) -> Vector3[]. */
  findPath(from, to, out = null) {
    const arr = out || [];
    const n = this.findPathInto(from, to, arr);
    arr.length = n;
    return arr;
  }

  /**
   * Igual ao findPath mas NAO trunca o array: devolve a contagem e reaproveita
   * os Vector3 ja existentes. E a versao que os bots usam - repath acontece
   * dentro do fixedUpdate e alocar la e proibido.
   */
  findPathInto(from, to, out) {
    if (!this.ready || !from || !to || !Array.isArray(out)) return 0;

    let start = this._findCell(from.x, from.z, from.y, 6);
    let goal = this._findCell(to.x, to.z, to.y, 6);
    if (start < 0 || goal < 0) return 0;

    const reg = this.region[start];
    if (this.region[goal] !== reg) {
      goal = this._nearestInRegion(goal, reg, 14);
      if (goal < 0) return 0;
    }

    if (start === goal) {
      this._setPoint(out, 0, to.x, this.height[goal], to.z);
      return 1;
    }

    const cellCount = this._astar(start, goal);
    if (cellCount <= 0) return 0;

    // --- polilinha bruta: comeca no pe do bot, termina no destino pedido ----
    let n = 0;
    this._setPoint(out, n++, from.x, this.height[start], from.z);
    for (let k = 1; k < cellCount - 1; k++) {
      const c = this._cells[k];
      this._setPoint(out, n++, this.cellCenterX(c), this.height[c], this.cellCenterZ(c));
    }
    const last = this._cells[cellCount - 1];
    const lastIsGoalCell = (this.cellIndexAt(to.x, to.z) === last);
    if (lastIsGoalCell) this._setPoint(out, n++, to.x, this.height[last], to.z);
    else this._setPoint(out, n++, this.cellCenterX(last), this.height[last], this.cellCenterZ(last));

    n = this._smooth(out, n, reg);
    this._pushFromWalls(out, n);
    return n;
  }

  _setPoint(out, i, x, y, z) {
    let v = out[i];
    if (!v) { v = new THREE.Vector3(); out[i] = v; }
    v.set(x, y, z);
    return v;
  }

  _nearestInRegion(cell, reg, maxRing) {
    const cx = cell % this.cols;
    const cz = (cell / this.cols) | 0;
    for (let r = 1; r <= maxRing; r++) {
      for (let dz = -r; dz <= r; dz++) {
        const nz = cz + dz;
        if (nz < 0 || nz >= this.rows) continue;
        const edge = (dz === -r || dz === r);
        for (let dx = -r; dx <= r; dx++) {
          if (!edge && dx !== -r && dx !== r) continue;
          const nx = cx + dx;
          if (nx < 0 || nx >= this.cols) continue;
          const j = nz * this.cols + nx;
          if (this.walk[j] && this.region[j] === reg) return j;
        }
      }
    }
    return -1;
  }

  /** Heuristica octogonal (8 direcoes): admissivel e muito mais justa que Manhattan. */
  _heuristic(a, b) {
    const ax = a % this.cols, az = (a / this.cols) | 0;
    const bx = b % this.cols, bz = (b / this.cols) | 0;
    const dx = Math.abs(ax - bx);
    const dz = Math.abs(az - bz);
    const lo = dx < dz ? dx : dz;
    const hi = dx < dz ? dz : dx;
    return (hi - lo + lo * SQRT2) * this.cell;
  }

  _astar(start, goal) {
    const sid = ++this._searchId;
    const cols = this.cols, rows = this.rows;
    const g = this._g, from = this._from, stamp = this._stamp, closed = this._closed;

    this._heapSize = 0;
    g[start] = 0;
    from[start] = -1;
    stamp[start] = sid;
    this._heapPush(start, this._heuristic(start, goal));

    let expanded = 0;
    let found = false;
    let bestNode = start;
    let bestH = this._heuristic(start, goal);

    while (this._heapSize > 0 && expanded < MAX_EXPAND) {
      const cur = this._heapPop();
      if (closed[cur] === sid) continue;
      closed[cur] = sid;
      expanded++;

      if (cur === goal) { found = true; break; }

      const cx = cur % cols;
      const cz = (cur / cols) | 0;
      const hc = this.height[cur];
      const gc = g[cur];

      for (let dz = -1; dz <= 1; dz++) {
        const nz = cz + dz;
        if (nz < 0 || nz >= rows) continue;
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dz === 0) continue;
          const nx = cx + dx;
          if (nx < 0 || nx >= cols) continue;
          const j = nz * cols + nx;
          if (closed[j] === sid || !this.walk[j]) continue;

          const dh = this.height[j] - hc;
          if (dh > STEP_UP || dh < -STEP_DOWN) continue;

          const diag = (dx !== 0 && dz !== 0);
          if (diag) {
            // sem cortar quina: as duas ortogonais tem que estar livres tambem
            const o1 = cz * cols + nx;
            const o2 = nz * cols + cx;
            if (!this.walk[o1] || !this.walk[o2]) continue;
            if (Math.abs(this.height[o1] - hc) > STEP_UP || Math.abs(this.height[o2] - hc) > STEP_UP) continue;
          }

          // custo: distancia + subida + repulsao de parede
          const step = (diag ? SQRT2 : 1) * this.cell;
          const cost = step + Math.abs(dh) * 1.4 + this.penalty[j] * this.cell * 0.16;
          const ng = gc + cost;

          if (stamp[j] !== sid || ng < g[j]) {
            stamp[j] = sid;
            g[j] = ng;
            from[j] = cur;
            const h = this._heuristic(j, goal);
            if (h < bestH) { bestH = h; bestNode = j; }
            this._heapPush(j, ng + h * 1.06); // peso 1.06: quase otimo, bem mais rapido
          }
        }
      }
    }

    // Caminho parcial e melhor que ficar parado: se nao chegou, vai ate o no
    // mais proximo do alvo que a busca alcancou.
    const end = found ? goal : bestNode;
    if (end === start) return 0;

    let count = 0;
    let node = end;
    while (node >= 0 && count < this._cells.length) {
      this._cells[count++] = node;
      if (node === start) break;
      node = from[node];
    }
    if (count < 2) return 0;

    // inverte no lugar
    for (let a = 0, b = count - 1; a < b; a++, b--) {
      const t = this._cells[a]; this._cells[a] = this._cells[b]; this._cells[b] = t;
    }
    return count;
  }

  _heapPush(idx, key) {
    const H = this._heapIdx, K = this._heapKey;
    if (this._heapSize >= H.length) return; // protecao: nunca estourar o buffer
    let i = this._heapSize++;
    H[i] = idx; K[i] = key;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (K[p] <= K[i]) break;
      const ti = H[p]; H[p] = H[i]; H[i] = ti;
      const tk = K[p]; K[p] = K[i]; K[i] = tk;
      i = p;
    }
  }

  _heapPop() {
    const H = this._heapIdx, K = this._heapKey;
    const top = H[0];
    const n = --this._heapSize;
    H[0] = H[n]; K[0] = K[n];
    let i = 0;
    for (;;) {
      const l = i * 2 + 1;
      const r = l + 1;
      let m = i;
      if (l < n && K[l] < K[m]) m = l;
      if (r < n && K[r] < K[m]) m = r;
      if (m === i) break;
      const ti = H[m]; H[m] = H[i]; H[i] = ti;
      const tk = K[m]; K[m] = K[i]; K[i] = tk;
      i = m;
    }
    return top;
  }

  // =========================================================================
  // acabamento do caminho
  // =========================================================================

  /**
   * Line-of-sight pull. Do ponto atual, olha o mais longe possivel e descarta
   * todo mundo no meio se a reta for andavel. E o que tira o "ziguezague de
   * grade" e faz o bot cortar a esquina como gente.
   * A janela e limitada (LOOKAHEAD) para o custo nao virar O(n^2) sem teto.
   */
  _smooth(points, count, reg) {
    if (count <= 2) return count;
    const LOOKAHEAD = 12;
    let write = 1;
    let i = 0;
    while (i < count - 1) {
      const a = points[i];
      let best = i + 1;
      const limit = Math.min(count - 1, i + LOOKAHEAD);
      for (let j = limit; j > i + 1; j--) {
        const b = points[j];
        if (this._lineWalkable(a.x, a.z, b.x, b.z, reg)) { best = j; break; }
      }
      const bp = points[best];
      if (write !== best) this._setPoint(points, write, bp.x, bp.y, bp.z);
      write++;
      i = best;
    }
    return write;
  }

  /**
   * Reta andavel: amostra o centro e as duas laterais (largura do bot) a cada
   * meia celula, exigindo celula caminhavel, mesma regiao e continuidade de
   * altura. Sem as laterais o caminho suavizado passa raspando na quina e o
   * capsuleMove trava o bot na parede.
   */
  _lineWalkable(x0, z0, x1, z1, reg) {
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 1e-4) return true;
    const ux = dx / len, uz = dz / len;
    const px = -uz, pz = ux;
    const steps = Math.max(1, Math.ceil(len / (this.cell * 0.5)));

    let i0 = this.cellIndexAt(x0, z0);
    let prevH = i0 >= 0 ? this.height[i0] : NO_GROUND;

    for (let s = 1; s <= steps; s++) {
      const t = (s / steps) * len;
      const cx = x0 + ux * t;
      const cz = z0 + uz * t;
      for (let k = -1; k <= 1; k++) {
        const sx = cx + px * AGENT_HALF * k;
        const sz = cz + pz * AGENT_HALF * k;
        const j = this.cellIndexAt(sx, sz);
        if (j < 0 || !this.walk[j]) return false;
        if (reg >= 0 && this.region[j] !== reg) return false;
      }
      const jc = this.cellIndexAt(cx, cz);
      const h = this.height[jc];
      if (prevH !== NO_GROUND && Math.abs(h - prevH) > STEP_UP + 0.08) return false;
      prevH = h;
    }
    return true;
  }

  /** Empurra cada waypoint para longe das celulas bloqueadas vizinhas. */
  _pushFromWalls(points, count) {
    for (let i = 1; i < count - 1; i++) {
      const p = points[i];
      const ci = this.cellIndexAt(p.x, p.z);
      if (ci < 0 || this.penalty[ci] === 0) continue;
      const cx = ci % this.cols;
      const cz = (ci / this.cols) | 0;
      const hc = this.height[ci];
      let ax = 0, az = 0;
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dz === 0) continue;
          const nx = cx + dx, nz = cz + dz;
          let blocked = true;
          if (nx >= 0 && nz >= 0 && nx < this.cols && nz < this.rows) {
            const j = nz * this.cols + nx;
            blocked = !this.walk[j] || Math.abs(this.height[j] - hc) > STEP_UP;
          }
          if (blocked) { ax -= dx; az -= dz; }
        }
      }
      const m = Math.sqrt(ax * ax + az * az);
      if (m < 1e-4) continue;
      const push = Math.min(0.34, this.cell * 0.42);
      p.x += (ax / m) * push;
      p.z += (az / m) * push;
    }
  }

  // =========================================================================
  // cobertura
  // =========================================================================

  /**
   * Contrato: coverPointsNear(pos, threatPos, radius) -> Vector3[].
   *
   * Estrategia em duas fases porque lineOfSight custa raycast:
   *   fase 1 (barata): filtra celulas caminhaveis, na mesma regiao, dentro do
   *     raio, longe demais nao, coladas em algum obstaculo (penalty > 0), e
   *     pontua. Guarda so as melhores num buffer fixo.
   *   fase 2 (cara): testa lineOfSight das melhores ate a ameaca. Sem visao =
   *     tem parede no meio = e cobertura de verdade.
   */
  coverPointsNear(pos, threatPos, radius = 10, out = null) {
    const result = out || [];
    result.length = 0;
    if (!this.ready || !pos || !threatPos) return result;

    const startCell = this._findCell(pos.x, pos.z, pos.y, 6);
    if (startCell < 0) return result;
    const reg = this.region[startCell];

    const cx = startCell % this.cols;
    const cz = (startCell / this.cols) | 0;
    const rad = Math.max(2, Math.ceil(radius * this.invCell));
    const r2 = radius * radius;

    const candIdx = _candIdx;
    const candScore = _candScore;
    let candCount = 0;

    for (let dz = -rad; dz <= rad; dz++) {
      const nz = cz + dz;
      if (nz < 0 || nz >= this.rows) continue;
      for (let dx = -rad; dx <= rad; dx++) {
        const nx = cx + dx;
        if (nx < 0 || nx >= this.cols) continue;
        const j = nz * this.cols + nx;
        if (!this.walk[j] || this.region[j] !== reg) continue;
        // cobertura util encosta em alguma coisa
        const pen = this.penalty[j];
        if (pen === 0) continue;

        const wx = this.cellCenterX(j);
        const wz = this.cellCenterZ(j);
        const ddx = wx - pos.x, ddz = wz - pos.z;
        const distSelf2 = ddx * ddx + ddz * ddz;
        if (distSelf2 > r2) continue;

        const tdx = wx - threatPos.x, tdz = wz - threatPos.z;
        const distThreat2 = tdx * tdx + tdz * tdz;
        if (distThreat2 < 25) continue;      // colado na ameaca nao e cobertura
        if (distThreat2 > 3600) continue;    // longe demais para voltar a atirar

        // pontuacao: perto de mim, encostado no obstaculo, nem colado nem longe da ameaca
        const score = Math.sqrt(distSelf2) - pen * 0.55 + Math.abs(Math.sqrt(distThreat2) - 16) * 0.10;

        if (candCount < CAND_MAX) {
          candIdx[candCount] = j;
          candScore[candCount] = score;
          candCount++;
        } else {
          // substitui o pior
          let worst = 0;
          for (let k = 1; k < CAND_MAX; k++) if (candScore[k] > candScore[worst]) worst = k;
          if (score < candScore[worst]) { candIdx[worst] = j; candScore[worst] = score; }
        }
      }
    }
    if (candCount === 0) return result;

    // ordena por pontuacao (insertion sort: no maximo CAND_MAX itens)
    for (let a = 1; a < candCount; a++) {
      const si = candIdx[a], ss = candScore[a];
      let b = a - 1;
      while (b >= 0 && candScore[b] > ss) { candIdx[b + 1] = candIdx[b]; candScore[b + 1] = candScore[b]; b--; }
      candIdx[b + 1] = si; candScore[b + 1] = ss;
    }

    const los = this.ctx.collision?.lineOfSight;
    const maxTests = Math.min(candCount, 26);
    const maxOut = COVER_MAX;
    _pb.set(threatPos.x, threatPos.y + 1.25, threatPos.z);

    for (let k = 0; k < maxTests && result.length < maxOut; k++) {
      const j = candIdx[k];
      const wx = this.cellCenterX(j);
      const wz = this.cellCenterZ(j);
      const wy = this.height[j];
      // olho agachado: se dali, agachado, nao se ve a ameaca, o corpo esta protegido
      _pa.set(wx, wy + 1.02, wz);
      let visible = true;
      if (typeof los === 'function') {
        try {
          visible = !!los.call(this.ctx.collision, _pa, _pb);
        } catch (err) {
          visible = true;
        }
      } else {
        // sem lineOfSight, usa o proprio relevo: diferenca de altura ja protege
        visible = (this.height[j] + 1.0) > (threatPos.y + 0.6);
      }
      if (!visible) {
        result.push(_coverPool[result.length].set(wx, wy, wz));
      }
    }
    return result;
  }

  /**
   * Posto de espera: celula caminhavel ENCOSTADA em alguma coisa (parede, muro,
   * container, veiculo), perto de `pos`. E o coverPointsNear de quando NAO ha
   * ameaca conhecida - por isso nao gasta nenhum raycast de linha de visao.
   *
   * Existe porque bot parado no eixo de uma rua aberta le como manequim em
   * qualquer print. Soldado de verdade para com as costas em alguma coisa.
   *
   * @returns {THREE.Vector3|null} `out` preenchido, ou null se nao houver nada
   *   melhor do que onde ele ja esta.
   */
  restPointNear(pos, radius = 7, out = null) {
    if (!this.ready || !pos) return null;
    const dst = out || new THREE.Vector3();

    const startCell = this._findCell(pos.x, pos.z, pos.y, 6);
    if (startCell < 0) return null;
    const reg = this.region[startCell];
    const own = this.penalty[startCell];

    const cx = startCell % this.cols;
    const cz = (startCell / this.cols) | 0;
    const rad = Math.max(2, Math.ceil(radius * this.invCell));
    const r2 = radius * radius;

    let best = -1;
    let bestScore = Infinity;

    for (let dz = -rad; dz <= rad; dz++) {
      const nz = cz + dz;
      if (nz < 0 || nz >= this.rows) continue;
      for (let dx = -rad; dx <= rad; dx++) {
        const nx = cx + dx;
        if (nx < 0 || nx >= this.cols) continue;
        const j = nz * this.cols + nx;
        if (!this.walk[j] || this.region[j] !== reg) continue;
        const pen = this.penalty[j];
        // so vale a pena andar ate la se for MAIS abrigado do que aqui
        if (pen < 3 || pen <= own) continue;
        // beco sem saida (quase toda vizinhanca bloqueada) prende o bot
        if (pen >= 7) continue;

        const wx = this.cellCenterX(j);
        const wz = this.cellCenterZ(j);
        const ddx = wx - pos.x, ddz = wz - pos.z;
        const d2 = ddx * ddx + ddz * ddz;
        if (d2 > r2 || d2 < 1.2) continue;

        const score = Math.sqrt(d2) - pen * 0.9;
        if (score < bestScore) { bestScore = score; best = j; }
      }
    }

    if (best < 0) return null;
    dst.set(this.cellCenterX(best), this.height[best], this.cellCenterZ(best));
    return dst;
  }

  dispose() {
    this.walk = null;
    this.height = null;
    this.penalty = null;
    this.region = null;
    this._g = null;
    this._from = null;
    this._stamp = null;
    this._closed = null;
    this._heapIdx = null;
    this._heapKey = null;
    this._cells = null;
    this._queue = null;
    this.ready = false;
  }
}
